import React from 'react';
import styled from 'styled-components';
import { AccountModel, TransferResult } from '@block-explorer/entities';
import {
  AuctionContractIcon,
  FlexColumn,
  FlexRow,
  TableData,
  TableRow,
  TruncateBox,
} from '@make-software/cspr-design';
import {
  BodyText,
  Tooltip,
  formatTimestamp,
  formatTimestampAge,
} from '@block-explorer/ui';

import {
  Avatar,
  CopyButton,
  HashLink,
  TableDataAccountInfo,
  TableDataCurrencyAmount,
} from '../../../components';
import { ACCOUNT_DETAILS_PATH } from '../../../router/paths';
import { deriveAccountInfo, isValidAccountHash } from '../../../utils/account';
import { buildRouterPath, useRouterPath } from '../../../router/utils';
import { TableDataNamedKey } from '../../../components/table-data-named-key/table-data-named-key';
import { Trans, useTranslation } from 'react-i18next';
import { TransactionHashLink } from '../../../components/hash-link/transaction-hash-link.tsx';

const StyledTruncateBox = styled(TruncateBox)(() => ({
  height: '20px',
}));

const AuctionPoolAccountInfo = () => {
  const { t } = useTranslation();
  const hash = config.auction_pool_account_hash;
  const path = buildRouterPath(ACCOUNT_DETAILS_PATH, { id: hash }, undefined);
  return (
    <FlexRow align="center" itemsSpacing={12}>
      <Avatar path={AuctionContractIcon} />
      <Tooltip tooltipContent={hash} caption={t('Account hash')} monotype>
        <FlexColumn>
          <BodyText size={3} monotype>
            <HashLink
              href={path}
              hash={hash}
              ariaDescription={'Link to Account details'}
            />
          </BodyText>
          <FlexRow itemsSpacing={6} align={'center'}>
            <FlexRow>
              <StyledTruncateBox size={1}>
                <BodyText size={3} variation="darkGray" noWrap>
                  <Trans t={t}>Auction Pool</Trans>
                </BodyText>
              </StyledTruncateBox>
            </FlexRow>
          </FlexRow>
        </FlexColumn>
      </Tooltip>
    </FlexRow>
  );
};

interface TransferListItemProps {
  loading: boolean;
  data?: TransferResult | null;
  onClick?: (id: number) => void;
}

export const AccountTransferListItem = ({
  loading,
  data,
}: TransferListItemProps) => {
  const { t } = useTranslation();
  const {
    urlParams: { id },
  } = useRouterPath(ACCOUNT_DETAILS_PATH);

  const lowerCaseId = id?.toLowerCase();

  const accountHash = isValidAccountHash(lowerCaseId)
    ? lowerCaseId
    : AccountModel({
        publicKeyHex: lowerCaseId,
      }).getAccountHash();

  const fromAccount =
    data?.from_purse_public_key &&
    AccountModel({
      publicKeyHex: data.from_purse_public_key,
    }).getAccountHash();

  const isOutgoingAmount = fromAccount === accountHash;

  const recipientAccountInfo = deriveAccountInfo(
    data?.to_purse_account_info || data?.to_purse_centralized_account_info
  );
  const recipientLogo = recipientAccountInfo?.logo || null;
  const recipientName = recipientAccountInfo?.name;
  const recipientCsprName = data?.to_purse_cspr_name;

  const senderAccountInfo = deriveAccountInfo(
    data?.from_purse_account_info || data?.from_purse_centralized_account_info
  );
  const senderLogo = senderAccountInfo?.logo || null;
  const senderName = senderAccountInfo?.name;
  const senderCsprName = data?.from_purse_cspr_name;

  const isAuctionPoolAccount =
    data?.to_account_hash === config.auction_pool_account_hash;

  const transferId = data?.id != null ? data.id.toString() : null;

  return (
    <TableRow>
      <TableData>
        <Tooltip
          tooltipContent={data?.deploy_hash}
          caption={t('Transaction Hash')}
        >
          <BodyText size={3} monotype>
            {data && <TransactionHashLink isDetails hash={data.deploy_hash} />}
          </BodyText>
        </Tooltip>
      </TableData>
      <TableData>
        <Tooltip tooltipContent={data && formatTimestamp(data.timestamp)}>
          <BodyText size={3} variation="darkGray" noWrap loading={loading}>
            {data && formatTimestampAge(data.timestamp)}
          </BodyText>
        </Tooltip>
      </TableData>
      <TableData>
        {data?.from_purse_public_key ? (
          <TableDataAccountInfo
            logo={senderLogo}
            name={senderName}
            publicKey={data?.from_purse_public_key}
            loading={!data}
            nameSize={1}
            csprName={senderCsprName}
          />
        ) : (
          <TableDataNamedKey purse={data?.from_purse || ''} />
        )}
      </TableData>
      <TableData>
        {isAuctionPoolAccount ? (
          <AuctionPoolAccountInfo />
        ) : data?.to_purse_public_key || data?.to_account_hash ? (
          <TableDataAccountInfo
            logo={recipientLogo}
            name={recipientName}
            publicKey={data?.to_purse_public_key || data?.to_account_hash}
            loading={!data}
            nameSize={1}
            csprName={recipientCsprName}
          />
        ) : (
          <TableDataNamedKey purse={data?.to_purse || ''} />
        )}
      </TableData>
      <TableData>
        <FlexRow>
          <Tooltip tooltipContent={transferId}>
            <TruncateBox>
              <BodyText size={3} variation="darkGray" loading={loading}>
                {transferId}
              </BodyText>
            </TruncateBox>
          </Tooltip>
          {transferId && <CopyButton value={transferId} />}
        </FlexRow>
      </TableData>
      <TableData align={'right'}>
        <BodyText size={3} monotype noWrap loading={loading}>
          {data && (
            <TableDataCurrencyAmount
              outgoingAmount={isOutgoingAmount}
              amount={data.amount.toString()}
              tokenCurrencyRate={data.rate}
              loading={loading}
            />
          )}
        </BodyText>
      </TableData>
    </TableRow>
  );
};

export default AccountTransferListItem;
